import { c as _c } from "react/compiler-runtime";
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import { GrowthbookFlagsEnum } from 'growthbook/types';
import { pageRoute, type PageRouteValue } from 'shared/constants/routes.constants';
import { AdminRolesEnum } from 'shared/enums/user-roles-enum';
import useUser from 'shared/hooks/use-user';
import { getProfileSectionsAccess } from 'shared/utils/get-profile-sections-access';
import { isSIO } from 'shared/utils/is-sio';
export type PagePermissions = Partial<Record<PageRouteValue, boolean>>;
interface UsePermissionResult {
  isReady: boolean;
  pagePermissions: PagePermissions;
}
export const usePermission = () => {
  const $ = _c(94);
  const growthbook = useGrowthBook();
  const {
    checkAdminRoles,
    checkImpersonatorAdminRoles,
    isPaid,
    isWorkspaceOwner,
    isSupportImpersonation,
    isSubAccountImpersonation,
    isWsAssistant,
    isCustomer,
    isAffiliate,
    isStudent,
    isWsAdmin,
    isRootAdmin,
    isLoading
  } = useUser();
  const whiteLabelFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.WhiteLabel);
  const helpdeskFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.Helpdesk);
  const subscriptionInvoicesFeatureIsOn = useFeatureIsOn(GrowthbookFlagsEnum.SubscriptionInvoices);
  const isReady = !isLoading && growthbook.ready;
  let t0;
  if ($[0] !== checkAdminRoles) {
    t0 = checkAdminRoles([AdminRolesEnum.support, AdminRolesEnum.admin, AdminRolesEnum.superAdmin]);
    $[0] = checkAdminRoles;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isSupportOrAdmin = t0;
  let t1;
  if ($[2] !== checkAdminRoles) {
    t1 = checkAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin]);
    $[2] = checkAdminRoles;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const isAdmin = t1;
  let t2;
  if ($[4] !== checkAdminRoles) {
    t2 = checkAdminRoles([AdminRolesEnum.contentManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin]);
    $[4] = checkAdminRoles;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const adminTemplatesPermission = t2;
  let t3;
  if ($[6] !== checkAdminRoles) {
    t3 = checkAdminRoles([AdminRolesEnum.mailingManager, AdminRolesEnum.admin, AdminRolesEnum.superAdmin, AdminRolesEnum.support, AdminRolesEnum.contentManager]);
    $[6] = checkAdminRoles;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const adminCustomersPermission = t3;
  let t4;
  if ($[8] !== isCustomer || $[9] !== isRootAdmin || $[10] !== isSubAccountImpersonation || $[11] !== isWorkspaceOwner || $[12] !== whiteLabelFeatureIsOn) {
    t4 = whiteLabelFeatureIsOn && !isSubAccountImpersonation && isCustomer && isWorkspaceOwner && !isRootAdmin && isSIO();
    $[8] = isCustomer;
    $[9] = isRootAdmin;
    $[10] = isSubAccountImpersonation;
    $[11] = isWorkspaceOwner;
    $[12] = whiteLabelFeatureIsOn;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const agencyPermission = t4;
  let t5;
  if ($[14] !== isCustomer || $[15] !== isRootAdmin || $[16] !== isSubAccountImpersonation || $[17] !== isWorkspaceOwner || $[18] !== isWsAdmin || $[19] !== isWsAssistant || $[20] !== whiteLabelFeatureIsOn) {
    t5 = whiteLabelFeatureIsOn && !isSubAccountImpersonation && isCustomer && (isWorkspaceOwner || isWsAdmin || isWsAssistant) && !isRootAdmin && isSIO();
    $[14] = isCustomer;
    $[15] = isRootAdmin;
    $[16] = isSubAccountImpersonation;
    $[17] = isWorkspaceOwner;
    $[18] = isWsAdmin;
    $[19] = isWsAssistant;
    $[20] = whiteLabelFeatureIsOn;
    $[21] = t5;
  } else {
    t5 = $[21];
  }
  const agencyUsersPermission = t5;
  let t6;
  if ($[22] !== isAffiliate || $[23] !== isCustomer || $[24] !== isStudent || $[25] !== isWorkspaceOwner) {
    t6 = getProfileSectionsAccess({
      isCustomer,
      isAffiliate,
      isStudent,
      isWorkspaceOwner
    });
    $[22] = isAffiliate;
    $[23] = isCustomer;
    $[24] = isStudent;
    $[25] = isWorkspaceOwner;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  const {
    canEditProfileDetails,
    canEditTimeSettings
  } = t6;
  let t7;
  bb0: {
    if (isSubAccountImpersonation) {
      t7 = false;
      break bb0;
    }
    if (isSupportImpersonation) {
      let t8;
      if ($[27] !== checkImpersonatorAdminRoles) {
        t8 = checkImpersonatorAdminRoles([AdminRolesEnum.admin, AdminRolesEnum.superAdmin]);
        $[27] = checkImpersonatorAdminRoles;
        $[28] = t8;
      } else {
        t8 = $[28];
      }
      t7 = t8;
      break bb0;
    }
    t7 = isWorkspaceOwner;
  }
  const changePasswordPermission = t7;
  const t8 = (isWorkspaceOwner || isWsAdmin) && isCustomer;
  const t9 = isWorkspaceOwner && isAffiliate;
  let t10;
  if ($[29] !== isCustomer || $[30] !== isSubAccountImpersonation || $[31] !== isWorkspaceOwner) {
    t10 = isCustomer && isWorkspaceOwner && !isSubAccountImpersonation && isSIO();
    $[29] = isCustomer;
    $[30] = isSubAccountImpersonation;
    $[31] = isWorkspaceOwner;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  const t11 = agencyPermission && isPaid;
  let t12;
  if ($[33] !== isCustomer || $[34] !== isWsAssistant) {
    t12 = isCustomer && !isWsAssistant && isSIO();
    $[33] = isCustomer;
    $[34] = isWsAssistant;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] !== isCustomer || $[37] !== isWorkspaceOwner) {
    t13 = isCustomer && isWorkspaceOwner && isSIO();
    $[36] = isCustomer;
    $[37] = isWorkspaceOwner;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  let t14;
  if ($[39] !== isCustomer) {
    t14 = isCustomer && isSIO();
    $[39] = isCustomer;
    $[40] = t14;
  } else {
    t14 = $[40];
  }
  const t15 = !isWsAssistant && isAdmin;
  const t16 = (isWorkspaceOwner || isWsAdmin || isWsAssistant) && isRootAdmin;
  const t17 = canEditProfileDetails || canEditTimeSettings;
  const t18 = isCustomer && isWorkspaceOwner;
  const t19 = isCustomer && isWorkspaceOwner;
  const t20 = isCustomer && isWorkspaceOwner;
  const t21 = (isCustomer || isStudent) && isWorkspaceOwner;
  const t22 = (isCustomer || isStudent) && isWorkspaceOwner && subscriptionInvoicesFeatureIsOn;
  const t23 = isCustomer && (isWorkspaceOwner || isWsAdmin);
  const t24 = isCustomer && isWorkspaceOwner;
  let t25;
  if ($[41] !== isCustomer || $[42] !== isWorkspaceOwner) {
    t25 = isCustomer && isWorkspaceOwner && isSIO();
    $[41] = isCustomer;
    $[42] = isWorkspaceOwner;
    $[43] = t25;
  } else {
    t25 = $[43];
  }
  const t26 = isCustomer && isWorkspaceOwner;
  let t27;
  if ($[44] !== isCustomer || $[45] !== isWorkspaceOwner || $[46] !== isWsAdmin) {
    t27 = isCustomer && (isWorkspaceOwner || isWsAdmin) && isSIO();
    $[44] = isCustomer;
    $[45] = isWorkspaceOwner;
    $[46] = isWsAdmin;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  const t28 = isCustomer && (isWorkspaceOwner || isWsAdmin);
  const t29 = isCustomer && (isWorkspaceOwner || isWsAdmin);
  const t30 = isCustomer && (isWorkspaceOwner || isWsAdmin);
  let t31;
  if ($[48] !== isAffiliate || $[49] !== isCustomer || $[50] !== isWorkspaceOwner) {
    t31 = (isCustomer || isAffiliate) && isWorkspaceOwner && isSIO();
    $[48] = isAffiliate;
    $[49] = isCustomer;
    $[50] = isWorkspaceOwner;
    $[51] = t31;
  } else {
    t31 = $[51];
  }
  const t32 = isCustomer && (isWorkspaceOwner || isWsAdmin);
  const t33 = isCustomer && helpdeskFeatureIsOn;
  const t34 = isCustomer && helpdeskFeatureIsOn;
  const t35 = isCustomer && helpdeskFeatureIsOn;
  const t36 = isCustomer && helpdeskFeatureIsOn;
  const t37 = isCustomer && helpdeskFeatureIsOn;
  const t38 = isCustomer && helpdeskFeatureIsOn;
  let t39;
  if ($[52] !== adminCustomersPermission || $[53] !== adminTemplatesPermission || $[54] !== agencyPermission || $[55] !== agencyUsersPermission || $[56] !== changePasswordPermission || $[57] !== isCustomer || $[58] !== isSupportOrAdmin || $[59] !== t10 || $[60] !== t11 || $[61] !== t12 || $[62] !== t13 || $[63] !== t14 || $[64] !== t15 || $[65] !== t16 || $[66] !== t17 || $[67] !== t18 || $[68] !== t19 || $[69] !== t20 || $[70] !== t21 || $[71] !== t22 || $[72] !== t23 || $[73] !== t24 || $[74] !== t25 || $[75] !== t26 || $[76] !== t27 || $[77] !== t28 || $[78] !== t29 || $[79] !== t30 || $[80] !== t31 || $[81] !== t32 || $[82] !== t33 || $[83] !== t34 || $[84] !== t35 || $[85] !== t36 || $[86] !== t37 || $[87] !== t38 || $[88] !== t8 || $[89] !== t9) {
    t39 = {
      [pageRoute.Dashboard]: t8,
      [pageRoute.AffiliateDashboard]: t9,
      [pageRoute.Subaccounts]: t10,
      [pageRoute.AgencyUsers]: agencyUsersPermission,
      [pageRoute.AgencySettings]: agencyPermission,
      [pageRoute.AgencyPricePlans]: agencyPermission,
      [pageRoute.AgencyPricePlanDetails]: t11,
      [pageRoute.Contacts]: isCustomer,
      [pageRoute.AssistantContacts]: isCustomer,
      [pageRoute.ContactsBin]: isCustomer,
      [pageRoute.ContactsCreate]: isCustomer,
      [pageRoute.ContactsImport]: isCustomer,
      [pageRoute.ContactDetails]: isCustomer,
      [pageRoute.ContactEmailHistory]: isCustomer,
      [pageRoute.Tags]: isCustomer,
      [pageRoute.CrmPipeline]: isCustomer,
      [pageRoute.CrmPipelineDetails]: isCustomer,
      [pageRoute.Calendar]: isCustomer,
      [pageRoute.CalendarAvailability]: isCustomer,
      [pageRoute.CalendarBookings]: isCustomer,
      [pageRoute.CalendarCreate]: isCustomer,
      [pageRoute.CalendarEventDetails]: isCustomer,
      [pageRoute.Websites]: isCustomer,
      [pageRoute.Funnels]: isCustomer,
      [pageRoute.FunnelDetails]: isCustomer,
      [pageRoute.Stores]: isCustomer,
      [pageRoute.StoreDetails]: isCustomer,
      [pageRoute.Blogs]: isCustomer,
      [pageRoute.BlogDetails]: isCustomer,
      [pageRoute.Newsletters]: isCustomer,
      [pageRoute.NewsletterEdit]: isCustomer,
      [pageRoute.Campaigns]: isCustomer,
      [pageRoute.CampaignDetails]: isCustomer,
      [pageRoute.CampaignStepEdit]: isCustomer,
      [pageRoute.EmailStatistics]: isCustomer,
      [pageRoute.SmsTemplates]: isCustomer,
      [pageRoute.SmsTemplatesCreate]: isCustomer,
      [pageRoute.SmsTemplateDetails]: isCustomer,
      [pageRoute.SmsStatistics]: isCustomer,
      [pageRoute.SmsStatisticsDetails]: isCustomer,
      [pageRoute.AutomationRules]: isCustomer,
      [pageRoute.AutomationRulesCreate]: isCustomer,
      [pageRoute.AutomationRuleDetails]: isCustomer,
      [pageRoute.Workflows]: isCustomer,
      [pageRoute.WorkflowEmailDetails]: isCustomer,
      [pageRoute.WorkflowEmailWithRecipientDetails]: isCustomer,
      [pageRoute.WorkflowSmsDetails]: isCustomer,
      [pageRoute.Products]: isCustomer,
      [pageRoute.ProductsCreate]: isCustomer,
      [pageRoute.ProductDetails]: isCustomer,
      [pageRoute.Coupons]: isCustomer,
      [pageRoute.CouponDetails]: isCustomer,
      [pageRoute.Courses]: isCustomer,
      [pageRoute.CoursesAssignments]: isCustomer,
      [pageRoute.CoursesBundles]: isCustomer,
      [pageRoute.CoursesStudents]: isCustomer,
      [pageRoute.CourseBundleDetails]: isCustomer,
      [pageRoute.CourseDetails]: isCustomer,
      [pageRoute.CourseCurriculum]: isCustomer,
      [pageRoute.CourseCurriculumAssignmentCreate]: isCustomer,
      [pageRoute.CourseCurriculumAssignmentEdit]: isCustomer,
      [pageRoute.CourseCurriculumEmail]: isCustomer,
      [pageRoute.CourseLightCurriculum]: isCustomer,
      [pageRoute.CourseLightCurriculumLecture]: isCustomer,
      [pageRoute.CourseLightSettings]: isCustomer,
      [pageRoute.CourseQuizDetails]: isCustomer,
      [pageRoute.CourseQuizResults]: isCustomer,
      [pageRoute.CourseQuizStudentResults]: isCustomer,
      [pageRoute.CoursesStudentDetails]: isCustomer,
      [pageRoute.Communities]: isCustomer,
      [pageRoute.Files]: isCustomer,
      [pageRoute.Orders]: isCustomer,
      [pageRoute.Transactions]: isCustomer,
      [pageRoute.Subscriptions]: isCustomer,
      [pageRoute.AffiliateInvoices]: t12,
      [pageRoute.AffiliateProgram]: t13,
      [pageRoute.AffiliateProgramDetails]: t14,
      [pageRoute.AdminUsers]: isSupportOrAdmin,
      [pageRoute.AdminEmailTemplates]: adminTemplatesPermission,
      [pageRoute.AdminPageTemplates]: adminTemplatesPermission,
      [pageRoute.AdminBlogTemplates]: adminTemplatesPermission,
      [pageRoute.AdminWebsiteTemplates]: adminTemplatesPermission,
      [pageRoute.AdminFunnelTemplates]: adminTemplatesPermission,
      [pageRoute.AdminCustomers]: adminCustomersPermission,
      [pageRoute.AdminEmailStats]: isSupportOrAdmin,
      [pageRoute.AdminEmailPendingMailing]: isSupportOrAdmin,
      [pageRoute.ManagementEmailPendingMailingDetails]: isSupportOrAdmin,
      [pageRoute.ManagementEmailStatsMailingDetails]: isSupportOrAdmin,
      [pageRoute.ManagementEmailStatsUserDetails]: isSupportOrAdmin,
      [pageRoute.AdminUserPlans]: t15,
      [pageRoute.AdminDomainsBlocked]: isSupportOrAdmin,
      [pageRoute.AdminDomainsWhitelist]: isSupportOrAdmin,
      [pageRoute.AdminSuspiciousPageKeywords]: isSupportOrAdmin,
      [pageRoute.AdminSuspiciousEmailKeywords]: isSupportOrAdmin,
      [pageRoute.AdminSuspiciousEmailAddress]: isSupportOrAdmin,
      [pageRoute.AdminStatistics]: t16,
      [pageRoute.ProfileUserSettings]: t17,
      [pageRoute.ProfileUserPlan]: t18,
      [pageRoute.ProfilePaymentGateways]: t19,
      [pageRoute.ProfilePaymentGatewaysPaypal]: t20,
      [pageRoute.ProfileManageSubscriptions]: t21,
      [pageRoute.ProfileManageSubscriptionInvoices]: t22,
      [pageRoute.ProfileMailingSettings]: isCustomer,
      [pageRoute.ProfileCourseSettings]: isCustomer,
      [pageRoute.ProfilePaymentSettings]: t23,
      [pageRoute.ProfileSalesFunnelsSettings]: isCustomer,
      [pageRoute.ProfileShippingSettings]: t24,
      [pageRoute.ProfileAffiliateProgramSettings]: t25,
      [pageRoute.ProfileCustomDomainsSettings]: t26,
      [pageRoute.ProfilePublicApiSettings]: t27,
      [pageRoute.ProfileWebhookSettings]: t28,
      [pageRoute.ProfileWebhookSettingsDetails]: t29,
      [pageRoute.ProfileIntegrations]: t30,
      [pageRoute.ProfileAffiliateSettings]: t31,
      [pageRoute.ProfileNotificationSettings]: true,
      [pageRoute.ProfileWorkspaceMembers]: t32,
      [pageRoute.ProfileChangePassword]: changePasswordPermission,
      [pageRoute.Helpdesk]: t33,
      [pageRoute.HelpdeskTags]: t34,
      [pageRoute.MailboxConnectionSettings]: t35,
      [pageRoute.MailboxEdit]: t36,
      [pageRoute.MailboxAutoReply]: t37,
      [pageRoute.HelpdeskInboxFolder]: t38
    };
    $[52] = adminCustomersPermission;
    $[53] = adminTemplatesPermission;
    $[54] = agencyPermission;
    $[55] = agencyUsersPermission;
    $[56] = changePasswordPermission;
    $[57] = isCustomer;
    $[58] = isSupportOrAdmin;
    $[59] = t10;
    $[60] = t11;
    $[61] = t12;
    $[62] = t13;
    $[63] = t14;
    $[64] = t15;
    $[65] = t16;
    $[66] = t17;
    $[67] = t18;
    $[68] = t19;
    $[69] = t20;
    $[70] = t21;
    $[71] = t22;
    $[72] = t23;
    $[73] = t24;
    $[74] = t25;
    $[75] = t26;
    $[76] = t27;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
    $[80] = t31;
    $[81] = t32;
    $[82] = t33;
    $[83] = t34;
    $[84] = t35;
    $[85] = t36;
    $[86] = t37;
    $[87] = t38;
    $[88] = t8;
    $[89] = t9;
    $[90] = t39;
  } else {
    t39 = $[90];
  }
  const pagePermissions = t39;
  let t40;
  if ($[91] !== isReady || $[92] !== pagePermissions) {
    t40 = {
      isReady,
      pagePermissions
    };
    $[91] = isReady;
    $[92] = pagePermissions;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  return t40;
};